import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { ButtonOutlined } from "../../../buttons/ButtonOutlined";
import {
  setPanelFilterBy,
  setPanelSortBy,
} from "../../../../redux/panel/panelSlice";
import { OutsideAlerter } from "../../../OutsideAlerter";
import { useTranslation } from "react-i18next";
import { namespaces } from "../../../../consts/i18n";
import {
  PANEL_FILTER_OPTIONS,
  PANEL_SORTING_OPTIONS,
} from "../../../../consts/panelFilterKeys";

export const PanelFilter = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation(namespaces.viewer);

  const sortBy = useSelector((state) => state.panel.sortBy);
  const filterBy = useSelector((state) => state.panel.filterBy);

  const [expandSortingOptions, setExpandSortingOptions] = useState(false);
  const [expandFilterOptions, setExpandFilterOptions] = useState(false);

  return (
    <section className="mt-4 flex justify-between items-center gap-2">
      {/* FILTERS */}
      <div className="flex gap-2">
        <div className="flex relative">
          <button
            onClick={() => setExpandFilterOptions(!expandFilterOptions)}
            className="inline-flex items-center w-full min-w-max justify-center gap-x-1.5 rounded-full bg-white px-3 py-2 text-xs font-bold text-black hover:shadow-sm"
            id="filter-by"
          >
            <span className="mr-1 text-xs" id="filter-by-text">
              {t("sidebar.filters.addPanelFilter")}
            </span>
            {expandFilterOptions ? (
              <img
                src="/images/sidebar_dropdown_selected.svg"
                alt=""
                id="filter-by-img"
              />
            ) : (
              <img
                src="/images/sidebar_dropdown.svg"
                alt=""
                id="filter-by-img"
              />
            )}
          </button>
          {expandFilterOptions && (
            <OutsideAlerter
              onClickOutside={(e) => {
                // TODO: Outside alerter not working as expected, id checks as a temp workaround. not ideal...
                if (
                  e.target.id === "filter-by" ||
                  e.target.id === "filter-by-text" ||
                  e.target.id === "filter-by-img"
                )
                  return;
                setExpandFilterOptions(false);
              }}
            >
              <div
                className="absolute left-0 z-10 mt-9 w-full origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                role="menu"
                aria-orientation="vertical"
                aria-labelledby="menu-button"
                tabIndex="-1"
              >
                <ul role="none">
                  {PANEL_FILTER_OPTIONS.map((filter, index) => (
                    <li key={index}>
                      <button
                        className="flex w-full justify-left items-center p-4 py-2 text-xs text-black font-semibold hover:bg-gray-100 transition-colors"
                        role="menuitem"
                        id={`menu-item-${index}`}
                        onClick={() => {
                          dispatch(setPanelFilterBy(filter.key));
                          setExpandFilterOptions(false);
                        }}
                      >
                        <span
                          className={`inline-block h-[10px] w-[10px] mr-2 ${
                            filter === filterBy ? "bg-primary" : "bg-slate-300"
                          } rounded-full`}
                        />
                        {t(filter.translationKey)}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </OutsideAlerter>
          )}
        </div>

        {/* When filter is added */}
        {filterBy && (
          <ButtonOutlined
            iconSrc="/images/sidebar_delete.svg"
            className="border-primary p-1"
            onClick={() => {
              dispatch(setPanelFilterBy(""));
            }}
          >
            {t(
              PANEL_FILTER_OPTIONS.find((f) => f.key === filterBy)
                ?.translationKey,
            )}
          </ButtonOutlined>
        )}
      </div>

      {/* SORTING ORDER */}
      <div className="relative inline-block text-left">
        <div className="flex justify-center items-center">
          <span className="text-sm mr-2">{t("sidebar.filters.panelSort")}</span>
          <button
            className="inline-flex items-center w-full justify-center gap-x-1.5 rounded-full bg-white px-3 py-2 text-xs font-bold text-black hover:shadow-sm "
            id="sort-by"
            aria-expanded="true"
            aria-haspopup="true"
            onClick={() => setExpandSortingOptions(!expandSortingOptions)}
          >
            {t(
              PANEL_SORTING_OPTIONS.find((f) => f.key === sortBy)
                ?.translationKey,
            )}
            {expandSortingOptions ? (
              <img src="/images/sidebar_dropdown_selected.svg" alt="" />
            ) : (
              <img src="/images/sidebar_dropdown.svg" alt="" />
            )}
          </button>
        </div>

        {expandSortingOptions && (
          <OutsideAlerter
            onClickOutside={(e) => {
              if (e.target.id === "sort-by") return;

              setExpandSortingOptions(false);
            }}
          >
            <div
              className="absolute right-0 z-10 mt-2 min-w-max origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
              role="menu"
              aria-orientation="vertical"
              aria-labelledby="menu-button"
              tabIndex="-1"
            >
              <ul role="none">
                {PANEL_SORTING_OPTIONS.map((option, index) => (
                  <li key={index}>
                    <button
                      className="flex justify-left items-center p-4 py-2 text-xs text-black font-semibold hover:bg-gray-100 transition-colors"
                      role="menuitem"
                      id={`menu-item-${index}`}
                      onClick={() => {
                        dispatch(setPanelSortBy(option.key));
                        setExpandSortingOptions(false);
                      }}
                    >
                      <span
                        className={`inline-block h-[10px] w-[10px] mr-2 ${
                          option.key === sortBy ? "bg-primary" : "bg-slate-300"
                        } rounded-full`}
                      />
                      {t(option.translationKey)}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </OutsideAlerter>
        )}
      </div>
    </section>
  );
};
